import React, { useMemo } from 'react';
import { Check, FileStack, HardDrive, Loader2, Server, Upload, X } from 'lucide-react';
import { cn } from '../../../lib/utils';
import type { ThemeColors } from '../../../contexts/ThemeContext';
import { formatFileSize, formatFileSizeProgress } from '../../../components/attachments/fileIconUtils';
import { Button } from '../../../components/ui';

export type ComposeSendPhase = 'idle' | 'preparing' | 'uploading' | 'sending';

export interface ComposeSendProgress {
  phase: ComposeSendPhase;
  /** 0–100 while preparing files or uploading; null when unknown. */
  percent: number | null;
  /** Short status line (kept for button / fallback). */
  detail?: string;
  /** Current attachment file name (prepare phase). */
  fileName?: string;
  /** 1-based index of current file. */
  fileIndex?: number;
  /** Total attachment count. */
  fileCount?: number;
  /** Bytes processed in current phase (encode or upload). */
  bytesLoaded?: number;
  /** Total bytes for current phase. */
  bytesTotal?: number;
  /** Elapsed ms since send started (optional). */
  elapsedMs?: number;
  /** Estimated remaining ms when rate is known. */
  etaMs?: number;
  /** Human tip for the current wait. */
  tip?: string;
}

const STEPS: Array<{
  id: Exclude<ComposeSendPhase, 'idle'>;
  label: string;
  shortLabel: string;
  Icon: React.ComponentType<{ className?: string }>;
}> = [
  { id: 'preparing', label: 'Prepare', shortLabel: 'Prep', Icon: FileStack },
  { id: 'uploading', label: 'Upload', shortLabel: 'Upload', Icon: Upload },
  { id: 'sending', label: 'Deliver', shortLabel: 'Send', Icon: Server },
];

export function composeSendButtonLabel(progress: ComposeSendProgress | null | undefined): string {
  if (!progress || progress.phase === 'idle') return 'Send';
  if (progress.phase === 'preparing') {
    return progress.percent != null ? `Preparing ${progress.percent}%` : 'Preparing…';
  }
  if (progress.phase === 'uploading') {
    return progress.percent != null ? `Uploading ${progress.percent}%` : 'Uploading…';
  }
  return 'Sending…';
}

function phaseHeadline(progress: ComposeSendProgress): string {
  if (progress.phase === 'preparing') return 'Preparing attachments';
  if (progress.phase === 'uploading') return 'Uploading message to server';
  return 'Delivering via mail server';
}

function phaseDescription(progress: ComposeSendProgress): string {
  if (progress.phase === 'preparing') {
    return 'Encoding files so they can be sent with the message.';
  }
  if (progress.phase === 'uploading') {
    return 'Transferring the message and attachments to u-comm.';
  }
  return 'Server is handing the message to your mailbox provider (SMTP).';
}

function formatDuration(ms: number): string {
  const totalSec = Math.max(0, Math.round(ms / 1000));
  if (totalSec < 60) return `${totalSec}s`;
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return sec > 0 ? `${min}m ${sec}s` : `${min}m`;
}

export interface ComposeSendProgressBannerProps {
  colors: ThemeColors;
  progress: ComposeSendProgress;
  /** When false, hide the Prepare step (no attachments). */
  hasAttachments?: boolean;
  /** Abort in-flight prepare / upload / send. */
  onCancel?: () => void;
}

const ComposeSendProgressBanner: React.FC<ComposeSendProgressBannerProps> = ({
  colors,
  progress,
  hasAttachments = true,
  onCancel,
}) => {
  const steps = useMemo(
    () => (hasAttachments ? STEPS : STEPS.filter((s) => s.id !== 'preparing')),
    [hasAttachments],
  );

  const activeIndex = Math.max(
    0,
    steps.findIndex((s) => s.id === progress.phase),
  );

  const barPercent =
    progress.percent != null ? Math.max(0, Math.min(100, progress.percent)) : null;

  const showDeterminate = barPercent != null && progress.phase !== 'sending';

  const bytesLabel =
    progress.bytesLoaded != null && progress.bytesTotal != null && progress.bytesTotal > 0
      ? formatFileSizeProgress(progress.bytesLoaded, progress.bytesTotal)
      : progress.bytesTotal != null && progress.bytesTotal > 0
        ? formatFileSize(progress.bytesTotal)
        : progress.bytesLoaded != null && progress.bytesLoaded > 0
          ? formatFileSize(progress.bytesLoaded)
          : null;

  const fileLabel =
    progress.fileName && progress.fileIndex != null && progress.fileCount != null
      ? `File ${progress.fileIndex} of ${progress.fileCount}`
      : progress.fileCount != null
        ? `${progress.fileCount} attachment${progress.fileCount === 1 ? '' : 's'}`
        : null;

  return (
    <div
      className={cn('border-t px-4 py-3 sm:px-5', colors.border, colors.headerBg)}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <div className="mx-auto flex max-w-3xl flex-col gap-3">
        <ol className="flex items-center gap-1 sm:gap-2">
          {steps.map((step, index) => {
            const done = index < activeIndex;
            const active = index === activeIndex;
            const Icon = step.Icon;
            return (
              <React.Fragment key={step.id}>
                {index > 0 && (
                  <div
                    className={cn(
                      'h-px min-w-[0.75rem] flex-1 rounded-full sm:min-w-[1.25rem]',
                      done || active ? colors.primary : colors.border,
                    )}
                    aria-hidden
                  />
                )}
                <li
                  className={cn(
                    'flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors sm:gap-2 sm:px-3',
                    active && cn(colors.primary, colors.primaryText, 'border-transparent shadow-sm'),
                    done && !active && cn(colors.success, 'border-transparent'),
                    !done && !active && cn(colors.border, colors.surface, colors.textMuted),
                  )}
                >
                  <span
                    className={cn(
                      'flex h-5 w-5 items-center justify-center rounded-full',
                      active && 'bg-white/20',
                    )}
                  >
                    {done ? (
                      <Check className="h-3.5 w-3.5" aria-hidden />
                    ) : active ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                    ) : (
                      <Icon className="h-3.5 w-3.5" aria-hidden />
                    )}
                  </span>
                  <span className="hidden sm:inline">{step.label}</span>
                  <span className="sm:hidden">{step.shortLabel}</span>
                  {active && showDeterminate && (
                    <span className="tabular-nums opacity-90">{barPercent}%</span>
                  )}
                </li>
              </React.Fragment>
            );
          })}
        </ol>

        <div className="space-y-2">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className={cn('text-sm font-semibold', colors.text)}>{phaseHeadline(progress)}</p>
              <p className={cn('mt-0.5 text-xs leading-relaxed', colors.textMuted)}>
                {phaseDescription(progress)}
              </p>
            </div>
            <div className="flex shrink-0 items-start gap-2">
              {showDeterminate && (
                <p className={cn('text-lg font-semibold tabular-nums leading-none', colors.primaryFg)}>
                  {barPercent}%
                </p>
              )}
              {onCancel && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={onCancel}
                  className={cn(
                    'h-8 gap-1 rounded-md px-2.5 text-xs font-medium',
                    colors.border,
                    colors.text,
                    colors.surface,
                  )}
                  title="Cancel send"
                  aria-label="Cancel send"
                >
                  <X className="h-3.5 w-3.5" aria-hidden />
                  Cancel
                </Button>
              )}
            </div>
          </div>

          <div className={cn('relative h-2 w-full overflow-hidden rounded-full', colors.surfaceHover)}>
            {showDeterminate ? (
              <div
                className={cn('h-full rounded-full transition-[width] duration-300 ease-out', colors.primary)}
                style={{ width: `${barPercent}%` }}
              />
            ) : (
              <div
                className={cn(
                  'absolute inset-y-0 w-1/3 rounded-full',
                  colors.primary,
                  'animate-[compose-send-indeterminate_1.2s_ease-in-out_infinite]',
                )}
              />
            )}
          </div>

          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs">
            {fileLabel && (
              <span className={cn('inline-flex items-center gap-1.5', colors.textSecondary)}>
                <FileStack className="h-3.5 w-3.5 shrink-0 opacity-70" aria-hidden />
                {fileLabel}
              </span>
            )}
            {progress.fileName && (
              <span className={cn('min-w-0 max-w-[14rem] truncate font-medium', colors.text)} title={progress.fileName}>
                {progress.fileName}
              </span>
            )}
            {bytesLabel && (
              <span className={cn('inline-flex items-center gap-1.5 tabular-nums', colors.textMuted)}>
                <HardDrive className="h-3.5 w-3.5 shrink-0 opacity-70" aria-hidden />
                {bytesLabel}
              </span>
            )}
            {progress.elapsedMs != null && progress.elapsedMs >= 1000 && (
              <span className={cn('tabular-nums', colors.textMuted)}>
                Elapsed {formatDuration(progress.elapsedMs)}
              </span>
            )}
            {progress.etaMs != null && progress.etaMs > 0 && progress.phase !== 'sending' && (
              <span className={cn('tabular-nums', colors.textMuted)}>
                ~{formatDuration(progress.etaMs)} left
              </span>
            )}
          </div>

          {(progress.tip || progress.phase === 'sending') && (
            <p className={cn('rounded-lg border px-2.5 py-1.5 text-[11px] leading-relaxed', colors.border, colors.surface, colors.textMuted)}>
              {progress.tip ||
                'Large attachments can take a while on the mail server. Keep this window open until send finishes.'}
            </p>
          )}
        </div>
      </div>

      <style>{`
        @keyframes compose-send-indeterminate {
          0% { left: -33%; }
          100% { left: 100%; }
        }
      `}</style>
    </div>
  );
};

export function isActiveSendPhase(
  progress: ComposeSendProgress | null | undefined,
): progress is ComposeSendProgress {
  return Boolean(progress && progress.phase !== 'idle');
}

export default ComposeSendProgressBanner;
